import type {
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  ListApiKeysResponse,
  RevokeApiKeyResponse,
  UpdateApiKeyRequest,
  UpdateApiKeyResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { createApiKey, listApiKeys, revokeApiKey, updateApiKey } from './keys.ts';

/**
 * React Query wrappers around the `/v1/keys` API.
 *
 * Query key conventions:
 *   - `['keys']`  — list query
 *
 * Mutation invalidation map:
 *   - createApiKey → invalidate `['keys']`
 *   - updateApiKey → invalidate `['keys']`
 *   - revokeApiKey → invalidate `['keys']`
 *
 * All three mutations touch the same list surface, so a single
 * invalidation is sufficient. A per-key detail query is unnecessary
 * in R4 — list rows carry every field the /keys page needs.
 */

export const KEYS_QUERY_KEY = ['keys'] as const;

export function useKeysList(enabled = true): UseQueryResult<ListApiKeysResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: KEYS_QUERY_KEY,
    queryFn: ({ signal }) => listApiKeys(fetchImpl, signal),
    enabled,
  });
}

export function useCreateApiKey(): UseMutationResult<
  CreateApiKeyResponse,
  Error,
  CreateApiKeyRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createApiKey(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
  });
}

export interface UpdateApiKeyVars {
  readonly id: string;
  readonly body: UpdateApiKeyRequest;
}

export function useUpdateApiKey(): UseMutationResult<
  UpdateApiKeyResponse,
  Error,
  UpdateApiKeyVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateApiKey(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
  });
}

export function useRevokeApiKey(): UseMutationResult<RevokeApiKeyResponse, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => revokeApiKey(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
    },
  });
}
